import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { ReceiptLongOutlined } from "@mui/icons-material";
import { getTopupDetail, evidenceUrl } from "api/financial";
import { getMyTopupDetail, branchCashEvidenceUrl } from "api/branchCash";
import { formatPaymentSource } from "utils/formatWalletTxnDetail";
import {
  money,
  fmtDate,
  SectionLabel,
  SOURCE_STYLE,
  SinglePaymentCard,
  ChangeGivenBox,
  RelatedTransactionsBox,
  SlipLightbox,
  HeroMetaHeader,
  DetailDialogShell,
} from "./receiptParts";
import TransactionDetailDialog from "./TransactionDetailDialog";

// Full detail of a completed (or pending/rejected) cash top-up, keyed to the
// WalletTopupRequest itself rather than one of its several ledger legs — same
// idea as ShippingApprovalDetailDialog being keyed to the ShippingPayment.
// Opened from the daily-summary feed's merged "Top Up" row, whose headline
// number is the CREDITED amount; this dialog's hero matches that, with the
// gross received amount + payment slip shown as supporting detail below (see
// TransactionDetailDialog for why a raw ledger leg can't show both coherently).
// `mode="financial"` (default) is the manager drill-down; `mode="branch"` is the
// station-scoped Branch Cash page — same UI, different endpoint, same split as
// TransactionDetailDialog/ShippingApprovalDetailDialog.
export default function TopupDetailDialog({ open, onClose, topupId, mode = "financial" }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [txId, setTxId] = useState(null);
  const fetchDetail = mode === "branch" ? getMyTopupDetail : getTopupDetail;
  const getEvidenceUrl = mode === "branch" ? branchCashEvidenceUrl : evidenceUrl;

  useEffect(() => {
    if (!open || !topupId) return;
    setLoading(true);
    setD(null);
    fetchDetail(topupId)
      .then((res) => setD(res && res.success ? res.data : null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topupId, mode]);

  const tu = d?.topup;
  // Multiple evidence photos are supported; older records only have the single
  // `evidencePhoto` — normalize both shapes.
  const photos = tu
    ? (tu.evidencePhotos?.length ? tu.evidencePhotos : (tu.evidencePhoto?.filename ? [tu.evidencePhoto] : []))
    : [];
  const ok = tu?.status === "approved";

  return (
    <>
      <DetailDialogShell
        t={t}
        open={open}
        onClose={onClose}
        title={t("wallet.admin.topUp")}
        subtitle={tu?.reference}
        loading={loading}
        empty={!tu}
      >
        {tu && (
          <>
            <HeroMetaHeader
              heroLabel={t("wallet.admin.amountCredited")}
              heroValue={money(tu.amount, tu.currency)}
              heroExtra={
                <Typography sx={{ fontSize: 12, mt: 0.5 }} color="text.secondary">
                  {t("wallet.admin.amountReceived")}: {money(tu.receivedAmount ?? tu.amount, tu.receivedCurrency || tu.currency)}
                </Typography>
              }
              metaRows={[
                { label: t("financial.customer"), value: d.customer ? `${d.customer.name}${d.customer.whatsapp ? ` · ${d.customer.whatsapp}` : ""}` : "—" },
                { label: t("financial.station"), value: d.station?.name || tu.stationId },
                { label: t("wallet.admin.paymentSource"), value: formatPaymentSource(tu.paymentSource, t) },
                { label: t("financial.date"), value: fmtDate(tu.createdAt) },
              ]}
            />

            {tu.description && (
              <Typography sx={{ fontSize: 12.5, mt: 1.5 }} color="text.secondary">
                {tu.description}
              </Typography>
            )}

            <SectionLabel Icon={ReceiptLongOutlined}>{t("financial.paymentLines")}</SectionLabel>
            <SinglePaymentCard
              t={t}
              label={formatPaymentSource(tu.paymentSource, t)}
              currency={tu.receivedCurrency || tu.currency}
              amount={tu.receivedAmount ?? tu.amount}
              style={SOURCE_STYLE[tu.paymentSource] || SOURCE_STYLE.cash_store}
              photos={photos}
              onEnlarge={setLightbox}
              getEvidenceUrl={getEvidenceUrl}
            />

            <ChangeGivenBox t={t} lines={tu.changeLines} />

            <RelatedTransactionsBox
              t={t}
              ok={ok}
              statusLabel={
                tu.status === "approved"
                  ? t("financial.completed")
                  : tu.status === "rejected"
                  ? t("financial.rejected")
                  : t("financial.pending")
              }
              subLabel={
                ok && tu.reviewedBy?.name
                  ? `${t("financial.approvedBy")}: ${tu.reviewedBy.name}${tu.reviewedBy.reviewedAt ? ` · ${fmtDate(tu.reviewedBy.reviewedAt)}` : ""}`
                  : tu.status === "rejected" && tu.rejectionReason
                  ? `${t("financial.rejectionReason")}: ${tu.rejectionReason}`
                  : tu.createdByStaff?.name
                  ? `${t("branchCash.requestedBy")}: ${tu.createdByStaff.name}`
                  : null
              }
              refs={d.transactionRefs}
              onView={setTxId}
            />
          </>
        )}
      </DetailDialogShell>

      <TransactionDetailDialog open={!!txId} txId={txId} mode={mode} onClose={() => setTxId(null)} />
      <SlipLightbox filename={lightbox} onClose={() => setLightbox(null)} t={t} getEvidenceUrl={getEvidenceUrl} />
    </>
  );
}
